import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Database, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import { PositionMappingService, type CreatePositionMappingData } from '@/services/PositionMappingService';
import { toast } from 'sonner';

interface SampleDataImporterProps {
  onImportComplete?: () => void;
}

// Sample data based on user's CSV structure
const sampleMappings: Array<{
  symbol: string;
  tvSize: number;
  ctSize: number;
  baseUnit: string;
  category: string;
}> = [
  // Forex majors
  { symbol: 'EURUSD', tvSize: 100, ctSize: 0.01, baseUnit: 'EUR', category: 'forex' },
  { symbol: 'GBPUSD', tvSize: 100, ctSize: 0.01, baseUnit: 'GBP', category: 'forex' },
  { symbol: 'USDJPY', tvSize: 100, ctSize: 0.01, baseUnit: 'USD', category: 'forex' },
  { symbol: 'USDCAD', tvSize: 100, ctSize: 0.01, baseUnit: 'USD', category: 'forex' },
  { symbol: 'AUDUSD', tvSize: 100, ctSize: 0.01, baseUnit: 'AUD', category: 'forex' },
  { symbol: 'NZDUSD', tvSize: 100, ctSize: 0.01, baseUnit: 'NZD', category: 'forex' },
  { symbol: 'USDCHF', tvSize: 100, ctSize: 0.01, baseUnit: 'USD', category: 'forex' },
  
  // Crypto pairs
  { symbol: 'ADAUSD', tvSize: 1, ctSize: 10, baseUnit: 'ADA', category: 'crypto' },
  { symbol: 'BTCUSD', tvSize: 1, ctSize: 0.001, baseUnit: 'BTC', category: 'crypto' },
  { symbol: 'ETHUSD', tvSize: 1, ctSize: 0.01, baseUnit: 'ETH', category: 'crypto' },
  { symbol: 'LTCUSD', tvSize: 1, ctSize: 0.1, baseUnit: 'LTC', category: 'crypto' },
  { symbol: 'DOTUSD', tvSize: 1, ctSize: 1, baseUnit: 'DOT', category: 'crypto' },
  
  // Commodities
  { symbol: 'XAUUSD', tvSize: 100, ctSize: 0.01, baseUnit: 'XAU', category: 'commodity' },
  { symbol: 'XAGUSD', tvSize: 100, ctSize: 0.1, baseUnit: 'XAG', category: 'commodity' },
  { symbol: 'OILUSD', tvSize: 100, ctSize: 1, baseUnit: 'OIL', category: 'commodity' },
  
  // Indices
  { symbol: 'SPX500', tvSize: 1, ctSize: 0.1, baseUnit: 'SPX', category: 'index' },
  { symbol: 'NDX100', tvSize: 1, ctSize: 0.1, baseUnit: 'NDX', category: 'index' },
  { symbol: 'DJI30', tvSize: 1, ctSize: 0.1, baseUnit: 'DJI', category: 'index' },
];

export function SampleDataImporter({ onImportComplete }: SampleDataImporterProps) {
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const importSampleData = async () => {
    setImporting(true);
    setImportResults(null);

    try {
      let successCount = 0;
      let failedCount = 0;
      const errors: string[] = [];

      for (const sample of sampleMappings) {
        try {
          // Calculate ratio: CT Size / TV Size
          const ratio = sample.ctSize / sample.tvSize;
          
          // Determine contract size based on category and symbol
          let contractSize = 1;
          switch (sample.category) {
            case 'forex':
              contractSize = 100000;
              break;
            case 'crypto':
              contractSize = 1;
              break;
            case 'commodity':
              if (sample.symbol === 'XAUUSD') {
                contractSize = 100;
              } else if (sample.symbol === 'XAGUSD') {
                contractSize = 5000;
              } else {
                contractSize = 1000; // Default for other commodities
              }
              break;
            case 'index':
              contractSize = 1;
              break;
          }

          const mappingData: CreatePositionMappingData = {
            symbol: sample.symbol,
            tv_to_ctrader_ratio: ratio,
            contract_size: contractSize,
            base_unit: sample.baseUnit,
            category: sample.category as any,
            is_active: true
          };

          await PositionMappingService.createMapping(mappingData);
          successCount++;
        } catch (error) {
          failedCount++;
          errors.push(`${sample.symbol}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      setImportResults({
        success: successCount,
        failed: failedCount,
        errors
      });

      if (successCount > 0) {
        // Clear cache to refresh mappings
        PositionMappingService.clearCache();
        toast.success(`Successfully imported ${successCount} sample mappings`);
        onImportComplete?.();
      }

      if (failedCount > 0) {
        toast.warning(`${failedCount} mappings failed to import`);
      }

    } catch (error) {
      console.error('Sample data import error:', error);
      toast.error('Failed to import sample data');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Sample Data Importer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Import sample position mappings based on common trading symbols. 
            This will add {sampleMappings.length} pre-configured mappings with correct TV→CT ratios.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <div className="font-medium">Forex ({sampleMappings.filter(m => m.category === 'forex').length})</div>
            <div className="text-muted-foreground">EURUSD, GBPUSD, etc.</div>
          </div>
          <div>
            <div className="font-medium">Crypto ({sampleMappings.filter(m => m.category === 'crypto').length})</div>
            <div className="text-muted-foreground">ADAUSD, BTCUSD, etc.</div>
          </div>
          <div>
            <div className="font-medium">Commodities ({sampleMappings.filter(m => m.category === 'commodity').length})</div>
            <div className="text-muted-foreground">XAUUSD, XAGUSD, etc.</div>
          </div>
          <div>
            <div className="font-medium">Indices ({sampleMappings.filter(m => m.category === 'index').length})</div>
            <div className="text-muted-foreground">SPX500, NDX100, etc.</div>
          </div>
        </div>

        <Button 
          onClick={importSampleData} 
          disabled={importing}
          className="w-full"
        >
          {importing ? (
            <>
              <Upload className="h-4 w-4 mr-2 animate-spin" />
              Importing Sample Data...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Import {sampleMappings.length} Sample Mappings
            </>
          )}
        </Button>

        {importResults && (
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              {importResults.success > 0 && (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Success: {importResults.success}
                </Badge>
              )}
              {importResults.failed > 0 && (
                <Badge variant="destructive">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Failed: {importResults.failed}
                </Badge>
              )}
            </div>

            {importResults.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <div className="font-medium text-red-800 mb-2">Import Errors:</div>
                <div className="text-sm text-red-700 space-y-1">
                  {importResults.errors.slice(0, 5).map((error, index) => (
                    <div key={index}>• {error}</div>
                  ))}
                  {importResults.errors.length > 5 && (
                    <div>... and {importResults.errors.length - 5} more errors</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}